import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import {
  Flex,
  GoodsCard,
  List,
  Spinner,
  Typography,
  useScreenSize,
  type ListRef,
} from '@banhala/ui-kit';
import { AnalyticsScreen } from 'shared/src/context';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useRootStackNavigation } from 'shared/src/hooks/useRootNavigation';
import { useRemoveLikeGoods } from 'shared/src/queries';
import { getThreeColGoodsListData } from 'shared/src/component/AtomicRenderer/ItemList/ThreeColGoodsList/getThreeColGoodsListData';
import { bridge, useNavigator } from 'shared/src/utils';
import type { GoodsRowCells, ListItem } from './types';
import type { GoodsCoreModel, IScreensLogging } from 'shared/src/api/types';
import type { FavoriteMarketWithNewGoods } from 'shared/src/api/favoriteMarketService/getFavoriteMarketsWithNewGoods/types';
import type { FavoriteMarketsOrder } from 'shared/src/api/favoriteMarketService/getFavoriteMarketsFilterOptions/types';
import { useFavoriteMarketsWithNewGoods } from '../../queries/useFavoriteMarketsWithNewGoods';
import { useFavoriteMarketsFilterOptions } from '../../queries/useFavoriteMarketsFilterOptions';
import { useFavoriteMarketsGoods } from '../../queries/useFavoriteMarketsGoods';
import { FavoriteMarketEmpty } from '../../../LikeMarketList/components/FavoriteMarketEmpty';
import { MarketFilterBar } from './components/MarketFilterBar';
import { MarketsSection } from './components/MarketsSection';
import { useLogApplyCategoryChipFilter } from './hooks/useLogApplyCategoryChipFilter';
import { useLogApplySort } from './hooks/useLogApplySort';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogClickLikeGoods } from './hooks/useLogClickLikeGoods';
import { useLogClickMarket } from './hooks/useLogClickMarket';
import { useLogClickMarketMore } from './hooks/useLogClickMarketMore';
import { useLogClickSort } from './hooks/useLogClickSort';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogViewNewFavorited } from './hooks/useLogViewNewFavorited';
import { useMarketSortBottomSheet } from './hooks/useMarketSortBottomSheet';

const SCREEN_NAME = 'NEW_FAVORITED';

interface LikeMarketProps {
  isActive: boolean;
}

export const LikeMarket = ({ isActive }: LikeMarketProps) => (
  <AnalyticsScreen screen={SCREEN_NAME}>
    <LikeMarketContent isActive={isActive} />
  </AnalyticsScreen>
);

const LikeMarketContent = ({ isActive }: LikeMarketProps) => {
  useLogViewNewFavorited(isActive);
  const logClickMarket = useLogClickMarket();
  const logClickMarketMore = useLogClickMarketMore();
  const logClickSort = useLogClickSort();
  const logApplySort = useLogApplySort();
  const logApplyCategoryChipFilter = useLogApplyCategoryChipFilter();
  const logClickGoods = useLogClickGoods();
  const logClickLikeGoods = useLogClickLikeGoods();
  const logDisplayGoods = useLogDisplayGoods();

  const { data: marketsData, refetch: refetchMarkets } =
    useFavoriteMarketsWithNewGoods();
  const { data: filterOptions, refetch: refetchFilterOptions } =
    useFavoriteMarketsFilterOptions();
  const navigation = useRootStackNavigation();
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();
  const { show: showSortBottomSheet } = useMarketSortBottomSheet();
  const { likeMap, setLikeMap } = useLikeMap();
  const { mutateAsync: removeLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    screen: SCREEN_NAME,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
  });

  const [selectedOrder, setSelectedOrder] =
    useState<FavoriteMarketsOrder | null>(null);
  const [selectedCategorySno, setSelectedCategorySno] = useState<number | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const listRef = useRef<ListRef<ListItem>>(null);
  const scrollYRef = useRef(0);
  const titleYRef = useRef(0);
  const filterYRef = useRef(0);

  useEffect(() => {
    if (filterOptions == null) return;
    if (selectedOrder == null && filterOptions.order[0]) {
      setSelectedOrder(filterOptions.order[0]);
    }
  }, [filterOptions, selectedOrder]);

  const {
    data: goodsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchGoods,
  } = useFavoriteMarketsGoods({
    order: selectedOrder?.key,
    categorySno: selectedCategorySno ?? undefined,
  });

  useEffect(() => {
    const items = goodsData?.items;
    if (items == null) return;
    setLikeMap(prev => {
      const next = { ...prev };
      items.forEach(({ item }) => {
        if (next[item.sno] == null) {
          next[item.sno] = item.like?.is_liked ?? false;
        }
      });
      return next;
    });
  }, [goodsData?.items, setLikeMap]);

  const selectedCategory = useMemo(
    () =>
      filterOptions?.category.find(c => c.sno === selectedCategorySno) ?? null,
    [filterOptions, selectedCategorySno],
  );

  const listData = useMemo<ListItem[]>(() => {
    const cells = getThreeColGoodsListData(goodsData?.items ?? []);
    const rows: ListItem[] = [{ type: 'filter' }];
    for (let i = 0; i < cells.length; i += 3) {
      rows.push({
        type: 'goodsRow',
        cells: cells.slice(i, i + 3),
        key: `row-${i}`,
      });
    }
    return rows;
  }, [goodsData?.items]);

  const itemWidth = useMemo(() => (screenWidth - 4) / 3, [screenWidth]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchMarkets(),
        refetchFilterOptions(),
        refetchGoods(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchMarkets, refetchFilterOptions, refetchGoods]);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollYRef.current = event.nativeEvent.contentOffset.y;
    },
    [],
  );

  const handleTitleLayout = useCallback((event: LayoutChangeEvent) => {
    titleYRef.current = event.nativeEvent.layout.y;
  }, []);

  const handleHeaderLayout = useCallback((event: LayoutChangeEvent) => {
    filterYRef.current = event.nativeEvent.layout.height;
  }, []);

  const handlePressCategory = useCallback(
    (sno: number | null) => {
      if (sno === selectedCategorySno) return;
      const category =
        sno == null
          ? null
          : (filterOptions?.category.find(c => c.sno === sno) ?? null);
      logApplyCategoryChipFilter({ category });
      setSelectedCategorySno(sno);
      const targetY =
        scrollYRef.current < filterYRef.current
          ? titleYRef.current
          : filterYRef.current;
      listRef.current?.scrollToOffset({ offset: targetY, animated: true });
    },
    [selectedCategorySno, filterOptions, logApplyCategoryChipFilter],
  );

  const handlePressMarket = useCallback(
    (market: FavoriteMarketWithNewGoods, index: number) => {
      if (marketsData != null) {
        logClickMarket({ market, index, listId: marketsData.list_id });
      }
      navigator.moveToMarketHome({ sno: market.sno });
    },
    [marketsData, logClickMarket, navigator],
  );

  const handlePressGoods = useCallback(
    (item: GoodsCoreModel, logging: IScreensLogging) => {
      logClickGoods({ item, logging });
      navigator.moveToGoods({ sno: item.sno });
    },
    [logClickGoods, navigator],
  );

  const handlePressLike = useCallback(
    async (item: GoodsCoreModel, logging: IScreensLogging) => {
      const isLiked = likeMap[item.sno] ?? false;
      logClickLikeGoods({ item, logging, currentLike: isLiked });
      if (isLiked) {
        await removeLikeGoods({
          sno: item.sno,
          list_params_referrer: logging.analytics?.LIST_ID as
            | string
            | undefined,
        });
        bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
        setLikeMap(prev => ({ ...prev, [item.sno]: false }));
        return;
      }
      await addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
    },
    [likeMap, logClickLikeGoods, removeLikeGoods, setLikeMap, addLikeGoods],
  );

  const handlePressMore = useCallback(() => {
    logClickMarketMore();
    navigation.push('LikeMarketList', {});
  }, [logClickMarketMore, navigation]);

  const handlePressSort = useCallback(async () => {
    if (filterOptions == null || selectedOrder == null) return;
    logClickSort({ sortKey: selectedOrder.key });
    const result = await showSortBottomSheet({
      orders: filterOptions.order,
      selectedKey: selectedOrder.key,
    });
    if (result.type !== 'confirm') return;
    logApplySort({
      sortKey: result.data.selected.key,
      category: selectedCategory,
    });
    setSelectedOrder(result.data.selected);
  }, [
    filterOptions,
    selectedOrder,
    showSortBottomSheet,
    logClickSort,
    logApplySort,
    selectedCategory,
  ]);

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === 'filter') {
        if (selectedOrder == null || filterOptions == null) return null;
        return (
          <MarketFilterBar
            selectedOrder={selectedOrder}
            categories={filterOptions.category}
            selectedCategorySno={selectedCategorySno}
            onPressSort={handlePressSort}
            onPressCategory={handlePressCategory}
          />
        );
      }
      return (
        <GoodsRow
          cells={item.cells}
          itemWidth={itemWidth}
          likeMap={likeMap}
          onPressGoods={handlePressGoods}
          onPressLike={handlePressLike}
        />
      );
    },
    [
      selectedOrder,
      filterOptions,
      selectedCategorySno,
      handlePressSort,
      handlePressCategory,
      itemWidth,
      likeMap,
      handlePressGoods,
      handlePressLike,
    ],
  );

  const isInitialLoading =
    marketsData == null || filterOptions == null || goodsData == null;

  if (isInitialLoading) {
    return (
      <Flex.Center flex={1} backgroundColor={'content_always_white'}>
        <Spinner color={'content_tertiary'} size={24} />
      </Flex.Center>
    );
  }

  if (marketsData.markets.length === 0) {
    return (
      <Flex flex={1} backgroundColor={'content_always_white'}>
        <FavoriteMarketEmpty />
      </Flex>
    );
  }

  return (
    <List<ListItem>
      ref={listRef}
      data={listData}
      keyExtractor={item => (item.type === 'filter' ? 'filter' : item.key)}
      stickyHeaderIndices={[1]}
      showsVerticalScrollIndicator={false}
      style={{ backgroundColor: 'white' }}
      ListHeaderComponent={
        <Flex onLayout={handleHeaderLayout}>
          <MarketsSection
            markets={marketsData.markets}
            onPressMarket={handlePressMarket}
            onPressMore={handlePressMore}
          />
          <SectionTitle onLayout={handleTitleLayout} />
        </Flex>
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Flex.Center padding={{ vertical: 16 }}>
            <Spinner color={'content_tertiary'} size={20} />
          </Flex.Center>
        ) : null
      }
      ItemSeparatorComponent={GoodsRowSeparator}
      renderItem={renderItem}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      onEndReachedThreshold={1}
      refreshing={isRefreshing}
      onRefresh={handleRefresh}
      onViewableItemsChanged={logDisplayGoods}
      viewabilityConfig={{
        minimumViewTime: 500,
        itemVisiblePercentThreshold: 50,
      }}
    />

  );
};

const GoodsRowSeparator = () => <Flex height={2} />;

const SectionTitle = ({
  onLayout,
}: {
  onLayout?: (event: LayoutChangeEvent) => void;
}) => (
  <Flex
    backgroundColor={'content_always_white'}
    padding={{ top: 24, horizontal: 16, bottom: 0 }}
    onLayout={onLayout}
  >
    <Typography type={'h5'} color={'content_primary'}>
      찜한 마켓 상품 모아보기
    </Typography>
  </Flex>
);

interface GoodsRowProps {
  cells: GoodsRowCells;
  itemWidth: number;
  likeMap: Record<string, boolean>;
  onPressGoods: (item: GoodsCoreModel, logging: IScreensLogging) => void;
  onPressLike: (
    item: GoodsCoreModel,
    logging: IScreensLogging,
  ) => Promise<void>;
}

const GoodsRow = ({
  cells,
  itemWidth,
  likeMap,
  onPressGoods,
  onPressLike,
}: GoodsRowProps) => (
  <Flex horizontal gap={2}>
    {cells.map(cell =>
      cell.type === 'placeholder' ? (
        <Flex key={cell.key} width={itemWidth} />
      ) : (
        <Flex key={cell.data.sno} width={itemWidth}>
          <GoodsCard
            data={cell.data}
            size={'medium'}
            defaultWidth={itemWidth}
            isLike={likeMap[cell.data.sno]}
            onPress={() => onPressGoods(cell.data.item, cell.data.logging)}
            onPressLike={() => onPressLike(cell.data.item, cell.data.logging)}
          />
        </Flex>
      ),
    )}
  </Flex>
);
